import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	type AppKind,
	type AppStatus,
	GetEnabledAppKindsDocument,
	GetOrganizationAppsDocument,
	GetWorkspaceAppsDocument,
	RotateOrganizationAppCredentialDocument,
	UpdateOrganizationAppDocument,
	UpdateWorkspaceAppDocument,
} from "@/common/artemis-sdk/graphql";
import type { OrganizationApp, WorkspaceApp } from "../model";
import { useGqlClient } from "../use-gql-client";
import { useGitProviderConnection } from "./workspaces";

/** The organization's app catalog rows (org admin only). An app with no row is disabled. */
export function useOrganizationApps() {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["organizationApps"],
		queryFn: async () =>
			((await gqlFetch(GetOrganizationAppsDocument)).organizationApps ??
				[]) as OrganizationApp[],
	});
}

/** The app kinds enabled at the organization level — readable by any member. */
export function useEnabledAppKinds() {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["enabledAppKinds"],
		queryFn: async () =>
			((await gqlFetch(GetEnabledAppKindsDocument)).enabledAppKinds ??
				[]) as AppKind[],
	});
}

/** A workspace's app rows. An app with no row is disabled there. */
export function useWorkspaceApps(workspaceId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["workspaceApps", workspaceId],
		queryFn: async () =>
			((await gqlFetch(GetWorkspaceAppsDocument, { workspaceId }))
				.workspaceApps ?? []) as WorkspaceApp[],
		enabled: !!workspaceId,
	});
}

/**
 * Whether GitHub can be used in a workspace (ADR-062): the organization is connected, the GitHub
 * app is enabled for the organization, and enabled in this workspace. `connection` is the org's
 * connection (for linking repositories); `available` gates every GitHub action on workspace pages.
 */
export function useGitHubAvailable(workspaceId: string) {
	const { data: connection } = useGitProviderConnection();
	const { data: kinds } = useEnabledAppKinds();
	const { data: workspaceApps } = useWorkspaceApps(workspaceId);
	const available =
		!!connection &&
		!!kinds?.includes("GIT_HUB") &&
		!!workspaceApps?.some(
			(a) => a.kind === "GIT_HUB" && a.status === "ENABLED",
		);
	return { connection: connection ?? null, available };
}

/**
 * Enables or disables an app for the organization. The payload carries a one-time credential
 * reveal when this call generated one (Port's first enable).
 */
export function useUpdateOrganizationApp() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (variables: { kind: AppKind; status: AppStatus }) =>
			(
				await gqlFetch(UpdateOrganizationAppDocument, {
					kind: variables.kind,
					input: { status: variables.status },
				})
			).updateOrganizationApp,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["organizationApps"] });
			queryClient.invalidateQueries({ queryKey: ["enabledAppKinds"] });
		},
	});
}

/** Issues a new secret for an app's machine identity; the payload is the only reveal of it. */
export function useRotateOrganizationAppCredential() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (variables: {
			kind: AppKind;
			clientSecretExpiresAt?: string | null;
		}) =>
			(await gqlFetch(RotateOrganizationAppCredentialDocument, variables))
				.rotateOrganizationAppCredential,
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: ["organizationApps"] }),
	});
}

/** Enables or disables an org-enabled app in a workspace. */
export function useUpdateWorkspaceApp(workspaceId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (variables: { kind: AppKind; status: AppStatus }) =>
			(
				await gqlFetch(UpdateWorkspaceAppDocument, {
					workspaceId,
					kind: variables.kind,
					input: { status: variables.status },
				})
			).updateWorkspaceApp,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["workspaceApps", workspaceId],
			});
			queryClient.invalidateQueries({ queryKey: ["organizationApps"] });
		},
	});
}
